import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, FileText, Receipt, Key, Users, MessageSquare, MessageCircle, Bot, UserCog, LogOut, ChevronRight, Image as ImageIcon } from 'lucide-react';

interface SidebarProps {
  type: 'portal' | 'admin';
  active: string;
}

export default function Sidebar({ type, active }: SidebarProps) {
  const { t } = useLanguage();
  const { user, logout } = useAuth();

  const portalLinks = [
    { key: 'dashboard', icon: LayoutDashboard, path: '/portal', label: t('portal.dashboard') },
    { key: 'quotes', icon: FileText, path: '/portal/quotes', label: t('portal.quotes') },
    { key: 'invoices', icon: Receipt, path: '/portal/invoices', label: t('portal.invoices') },
    { key: 'licenses', icon: Key, path: '/portal/licenses', label: t('portal.licenses') },
  ];

  const adminLinks = [
    { key: 'dashboard', icon: LayoutDashboard, path: '/admin', label: t('admin.dashboard') },
    { key: 'quotes', icon: FileText, path: '/admin/quotes', label: t('admin.quotes') },
    { key: 'users', icon: Users, path: '/admin/users', label: t('admin.users') },
    { key: 'invoices', icon: Receipt, path: '/admin/invoices', label: t('admin.invoices') },
    { key: 'licenses', icon: Key, path: '/admin/licenses', label: t('admin.licenses') },
    { key: 'products', icon: ImageIcon, path: '/admin/products', label: 'Product Images' },
    { key: 'contacts', icon: MessageSquare, path: '/admin/contacts', label: t('admin.contacts') },
    { key: 'chat', icon: MessageCircle, path: '/admin/chat', label: 'Live Chat' },
    { key: 'chatbot', icon: Bot, path: '/admin/chatbot', label: 'AI Chatbot' },
    { key: 'team', icon: UserCog, path: '/admin/team', label: 'Team' },
  ];

  const links = type === 'admin' ? adminLinks : portalLinks;
  const accentColor = type === 'admin' ? 'var(--secondary)' : 'var(--primary)';

  return (
    <aside data-testid={`${type}-sidebar`} style={{
      width: 260,
      minHeight: '100vh',
      background: 'white',
      borderRight: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 24,
    }}>
      <div style={{ padding: '0 20px', marginBottom: 32 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 16 }}>
            <span style={{ color: 'var(--secondary)' }}>MALUZ</span> <span style={{ color: 'var(--primary)' }}>TECH</span>
          </span>
        </Link>
        {user && (
          <div style={{ marginTop: 16, padding: '12px', background: 'var(--bg-section)', border: '1px solid var(--border)' }}>
            <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{user.name}</p>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{user.email}</p>
          </div>
        )}
      </div>

      <nav style={{ flex: 1, padding: '0 12px' }}>
        {links.map(link => {
          const Icon = link.icon;
          const isActive = active === link.key;
          return (
            <Link
              key={link.key}
              to={link.path}
              data-testid={`${type}-nav-${link.key}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                marginBottom: 2,
                fontSize: 14,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? accentColor : 'var(--text-secondary)',
                background: isActive ? (type === 'admin' ? 'rgba(211,47,47,0.08)' : 'rgba(13,71,161,0.08)') : 'transparent',
                borderLeft: isActive ? `3px solid ${accentColor}` : '3px solid transparent',
                transition: 'all 0.15s',
              }}
            >
              <Icon size={18} />
              {link.label}
              {isActive && <ChevronRight size={14} style={{ marginLeft: 'auto' }} />}
            </Link>
          );
        })}
      </nav>

      <div style={{ padding: '20px', borderTop: '1px solid var(--border)' }}>
        <button
          onClick={logout}
          data-testid={`${type}-logout-btn`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            padding: '10px 12px',
            fontSize: 14,
            color: 'var(--text-muted)',
            background: 'none',
            border: 'none',
            transition: 'color 0.2s',
          }}
        >
          <LogOut size={18} />
          {t('nav.logout')}
        </button>
      </div>
    </aside>
  );
}
